// colors.ts

export interface ParsedColor {
  rgba: string
  r: number
  g: number
  b: number
  a: number
  h: number
  s: number
  l: number
}

let rawColors: string[] = []
const COLORS_EVENT = "raw-colors-changed"

export function setRawColors(colors: string[]) {
  rawColors = Array.isArray(colors) ? colors : []

  // avisa quem estiver ouvindo (React, etc)
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(COLORS_EVENT))
  }
}

export function getRawColors(): string[] {
  return rawColors
}

export function onRawColorsChange(cb: () => void) {
  if (typeof window === "undefined") return () => {}
  const handler = () => cb()
  window.addEventListener(COLORS_EVENT, handler)
  return () => window.removeEventListener(COLORS_EVENT, handler)
}

declare global {
  interface Window {
    setRawColors?: (colors: string[]) => void
  }
}

if (typeof window !== "undefined") {
  window.setRawColors = setRawColors
}

function parseColor(color: string): { r: number; g: number; b: number; a: number } | null {
  const trimmed = color.trim()

  const hexMatch = trimmed.match(/^#([0-9a-fA-F]{3,8})$/)
  if (hexMatch) {
    const hex = hexMatch[1]
    if (hex.length === 3) return { r: parseInt(hex[0] + hex[0], 16), g: parseInt(hex[1] + hex[1], 16), b: parseInt(hex[2] + hex[2], 16), a: 1 }
    if (hex.length === 4) return { r: parseInt(hex[0] + hex[0], 16), g: parseInt(hex[1] + hex[1], 16), b: parseInt(hex[2] + hex[2], 16), a: parseInt(hex[3] + hex[3], 16) / 255 }
    if (hex.length === 6) return { r: parseInt(hex.slice(0, 2), 16), g: parseInt(hex.slice(2, 4), 16), b: parseInt(hex.slice(4, 6), 16), a: 1 }
    if (hex.length === 8) return { r: parseInt(hex.slice(0, 2), 16), g: parseInt(hex.slice(2, 4), 16), b: parseInt(hex.slice(4, 6), 16), a: parseInt(hex.slice(6, 8), 16) / 255 }
    return null
  }

  const rgbaMatch = trimmed.match(
    /rgba?\(\s*(\d+)\s*[,/\s]\s*(\d+)\s*[,/\s]\s*(\d+)\s*(?:[,/\s]\s*([\d.]+%?))?\s*\)/
  )
  if (rgbaMatch) {
    let a = 1
    if (rgbaMatch[4] !== undefined) {
      a = rgbaMatch[4].endsWith("%") ? parseFloat(rgbaMatch[4]) / 100 : parseFloat(rgbaMatch[4])
    }
    return { r: parseInt(rgbaMatch[1]), g: parseInt(rgbaMatch[2]), b: parseInt(rgbaMatch[3]), a }
  }

  const hslaMatch = trimmed.match(
    /hsla?\(\s*([\d.]+)\s*[,/\s]\s*([\d.]+)%\s*[,/\s]\s*([\d.]+)%\s*(?:[,/\s]\s*([\d.]+%?))?\s*\)/
  )
  if (hslaMatch) {
    const h = parseFloat(hslaMatch[1])
    const s = parseFloat(hslaMatch[2]) / 100
    const l = parseFloat(hslaMatch[3]) / 100
    let a = 1
    if (hslaMatch[4] !== undefined) {
      a = hslaMatch[4].endsWith("%") ? parseFloat(hslaMatch[4]) / 100 : parseFloat(hslaMatch[4])
    }

    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2
    let r1 = 0, g1 = 0, b1 = 0
    if (h < 60) { r1 = c; g1 = x; b1 = 0 }
    else if (h < 120) { r1 = x; g1 = c; b1 = 0 }
    else if (h < 180) { r1 = 0; g1 = c; b1 = x }
    else if (h < 240) { r1 = 0; g1 = x; b1 = c }
    else if (h < 300) { r1 = x; g1 = 0; b1 = c }
    else { r1 = c; g1 = 0; b1 = x }

    return {
      r: Math.round((r1 + m) * 255),
      g: Math.round((g1 + m) * 255),
      b: Math.round((b1 + m) * 255),
      a,
    }
  }

  return null
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

  let h = 0
  switch (max) {
    case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
    case g: h = ((b - r) / d + 2) / 6; break
    case b: h = ((r - g) / d + 4) / 6; break
  }
  return { h: h * 360, s, l }
}

export function getParsedColors(): ParsedColor[] {
  if (!rawColors.length) return []
  return rawColors
    .map((raw) => {
      const parsed = parseColor(raw)
      if (!parsed) return null
      const { r, g, b, a } = parsed
      const { h, s, l } = rgbToHsl(r, g, b)
      return { rgba: raw, r, g, b, a, h, s, l }
    })
    .filter((c): c is ParsedColor => c !== null)
}

export { parseColor }
export { rgbToHsl }